import React, { useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet, RefreshControl } from 'react-native';
import {
  Card,
  Text,
  Button,
  ProgressBar,
  Chip,
  Surface,
  useTheme,
  ActivityIndicator,
  IconButton,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  Clock,
  Play,
  CheckCircle,
  Circle,
  Target,
  Coffee,
  BookOpen,
  Video,
  FileText,
  HelpCircle,
} from 'lucide-react-native';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchJourneyWeek } from '../../store/slices/journeySlice';
import type { JourneyActivity, ActivityType } from '@coffeeplexity/shared';

interface RouteParams {
  journeyId: string;
  weekNumber: number;
}

export function WeeklyGoalsScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useAppDispatch();

  const { journeyId, weekNumber } = route.params as RouteParams;
  const { currentWeek, loading } = useAppSelector(state => state.journey);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    dispatch(fetchJourneyWeek({ journeyId, weekNumber }));
  }, [dispatch, journeyId, weekNumber]);

  const onRefresh = async () => {
    setRefreshing(true);
    await dispatch(fetchJourneyWeek({ journeyId, weekNumber }));
    setRefreshing(false);
  };

  const handleActivityPress = (activity: JourneyActivity) => {
    (navigation as any).navigate('activityDetail', {
      activity: activity, // Pass the full activity object
      journeyId,
      weekNumber,
    });
  };

  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'video':
        return Video;
      case 'recipe':
        return Coffee;
      case 'reading':
        return BookOpen;
      case 'quiz':
        return HelpCircle;
      case 'practice':
        return Target;
      default:
        return FileText;
    }
  };

  const getActivityTypeColor = (type: ActivityType) => {
    switch (type) {
      case 'video':
        return theme.colors.error;
      case 'recipe':
        return theme.colors.primary;
      case 'reading':
        return theme.colors.tertiary;
      case 'quiz':
        return theme.colors.secondary;
      case 'practice':
        return theme.colors.outline;
      default:
        return theme.colors.onSurfaceVariant;
    }
  };

  const getActivityTypeLabel = (type: ActivityType) => {
    switch (type) {
      case 'video':
        return 'Video';
      case 'recipe':
        return 'Recipe';
      case 'reading':
        return 'Reading';
      case 'quiz':
        return 'Quiz';
      case 'practice':
        return 'Practice';
      default:
        return type;
    }
  };

  if (loading.week && !currentWeek) {
    return (
      <View
        style={[
          styles.container,
          styles.centered,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <ActivityIndicator size='large' />
        <Text style={styles.loadingText}>Loading week activities...</Text>
      </View>
    );
  }

  if (!currentWeek) {
    return (
      <View
        style={[
          styles.container,
          styles.centered,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <Text variant='headlineSmall'>Week not found</Text>
        <Button onPress={() => navigation.goBack()} style={styles.backButton}>
          Go Back
        </Button>
      </View>
    );
  }

  const completedActivities = currentWeek.activities.filter(
    (a: any) => a.is_completed
  ).length;
  const totalActivities = currentWeek.activities.length;
  const progressPercentage =
    totalActivities > 0 ? (completedActivities / totalActivities) * 100 : 0;
  const totalEstimatedTime = currentWeek.activities.reduce(
    (sum: number, a: any) => sum + a.estimated_minutes,
    0
  );

  return (
    <ScrollView
      style={[
        styles.container,
        { paddingTop: insets.top, backgroundColor: theme.colors.background },
      ]}
      contentContainerStyle={styles.contentContainer}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <View style={styles.titleContainer}>
            <Text variant='headlineMedium' style={styles.title}>
              {currentWeek.title}
            </Text>
            <Text variant='bodyMedium' style={styles.subtitle}>
              Week {currentWeek.week_number}
            </Text>
          </View>
          <IconButton
            icon='calendar'
            size={24}
            onPress={() => {
              /* TODO: Show calendar view */
            }}
          />
        </View>
        {currentWeek.description && (
          <Text variant='bodyMedium' style={styles.description}>
            {currentWeek.description}
          </Text>
        )}
      </View>

      {/* Progress Overview */}
      <Surface style={styles.progressCard} elevation={1}>
        <View style={styles.progressHeader}>
          <Text variant='titleMedium' style={styles.progressTitle}>
            Week Progress
          </Text>
          <Text variant='titleMedium' style={styles.progressPercentage}>
            {Math.round(progressPercentage)}%
          </Text>
        </View>
        <ProgressBar
          progress={progressPercentage / 100}
          color={theme.colors.primary}
          style={styles.progressBar}
        />
        <View style={styles.progressStats}>
          <View style={styles.statItem}>
            <CheckCircle size={16} color={theme.colors.primary} />
            <Text variant='bodySmall' style={styles.statText}>
              {completedActivities} of {totalActivities} completed
            </Text>
          </View>
          <View style={styles.statItem}>
            <Clock size={16} color={theme.colors.onSurfaceVariant} />
            <Text variant='bodySmall' style={styles.statText}>
              ~{Math.round(totalEstimatedTime / 60)}h {totalEstimatedTime % 60}m
              total
            </Text>
          </View>
        </View>
      </Surface>

      {/* Learning Objectives */}
      {currentWeek.learning_objectives.length > 0 && (
        <Surface style={styles.objectivesCard} elevation={1}>
          <Text variant='titleMedium' style={styles.objectivesTitle}>
            Learning Objectives
          </Text>
          {currentWeek.learning_objectives.map(
            (objective: string, index: number) => (
              <View key={index} style={styles.objectiveItem}>
                <Target size={16} color={theme.colors.primary} />
                <Text variant='bodyMedium' style={styles.objectiveText}>
                  {objective}
                </Text>
              </View>
            )
          )}
        </Surface>
      )}

      {/* Activities List */}
      <View style={styles.activitiesSection}>
        <Text variant='titleLarge' style={styles.sectionTitle}>
          Activities ({currentWeek.activities.length})
        </Text>

        {currentWeek.activities.length > 0 ? (
          [...currentWeek.activities]
            .sort((a: any, b: any) => a.order_index - b.order_index)
            .map((activity: any) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                onPress={() => handleActivityPress(activity)}
                theme={theme}
                getActivityIcon={getActivityIcon}
                getActivityTypeColor={getActivityTypeColor}
                getActivityTypeLabel={getActivityTypeLabel}
              />
            ))
        ) : (
          <Text variant='bodyMedium'>No activities found for this week.</Text>
        )}
      </View>

      {/* Week Summary */}
      {currentWeek.is_completed && (
        <Surface style={styles.summaryCard} elevation={1}>
          <View style={styles.summaryHeader}>
            <CheckCircle size={24} color={theme.colors.primary} />
            <Text variant='titleMedium' style={styles.summaryTitle}>
              Week Completed!
            </Text>
          </View>
          <Text variant='bodyMedium' style={styles.summaryText}>
            Great job! You've completed all activities for this week. Ready to
            move on to the next week?
          </Text>
          <Button
            mode='contained'
            onPress={() => {
              // Navigate to next week if available
              (navigation as any).navigate('weeklyGoals', {
                journeyId,
                weekNumber: weekNumber + 1,
              });
            }}
            style={styles.nextWeekButton}
          >
            Next Week
          </Button>
        </Surface>
      )}
    </ScrollView>
  );
}

interface ActivityCardProps {
  activity: JourneyActivity;
  onPress: () => void;
  theme: any;
  getActivityIcon: (type: ActivityType) => any;
  getActivityTypeColor: (type: ActivityType) => string;
  getActivityTypeLabel: (type: ActivityType) => string;
}

function ActivityCard({
  activity,
  onPress,
  theme,
  getActivityIcon,
  getActivityTypeColor,
  getActivityTypeLabel,
}: ActivityCardProps) {
  const IconComponent = getActivityIcon(activity.activity_type);
  const typeColor = getActivityTypeColor(activity.activity_type);
  const typeLabel = getActivityTypeLabel(activity.activity_type);

  const progressPercentage = activity.progress?.progress_percentage || 0;
  const isCompleted = activity.is_completed;
  const isInProgress = progressPercentage > 0 && !isCompleted;

  return (
    <Card style={styles.activityCard} onPress={onPress}>
      <Card.Content>
        <View style={styles.activityHeader}>
          <View
            style={[
              styles.activityIconContainer,
              { backgroundColor: theme.colors.surfaceVariant },
            ]}
          >
            <IconComponent size={20} color={typeColor} />
          </View>
          <View style={styles.activityInfo}>
            <Text variant='titleMedium' style={styles.activityTitle}>
              {activity.title}
            </Text>
            <View style={styles.activityMeta}>
              <Chip
                mode='outlined'
                compact
                textStyle={{ color: typeColor }}
                style={{ borderColor: typeColor }}
              >
                {typeLabel}
              </Chip>
              <View style={styles.metaItem}>
                <Clock size={14} color={theme.colors.onSurfaceVariant} />
                <Text variant='bodySmall' style={styles.metaText}>
                  {activity.estimated_minutes}min
                </Text>
              </View>
            </View>
          </View>
          <View style={styles.activityStatus}>
            {isCompleted ? (
              <CheckCircle size={24} color={theme.colors.primary} />
            ) : isInProgress ? (
              <Play size={24} color={theme.colors.secondary} />
            ) : (
              <Circle size={24} color={theme.colors.outline} />
            )}
          </View>
        </View>

        {activity.description && (
          <Text variant='bodySmall' style={styles.activityDescription}>
            {activity.description}
          </Text>
        )}

        {/* Progress Bar for In-Progress Activities */}
        {isInProgress && (
          <View style={styles.activityProgress}>
            <ProgressBar
              progress={progressPercentage / 100}
              color={theme.colors.secondary}
              style={styles.activityProgressBar}
            />
            <Text variant='bodySmall' style={styles.progressText}>
              {Math.round(progressPercentage)}% complete
            </Text>
          </View>
        )}

        {/* Required Equipment */}
        {activity.required_equipment.length > 0 && (
          <View style={styles.equipmentSection}>
            <Text variant='labelMedium' style={styles.equipmentLabel}>
              Required Equipment:
            </Text>
            <View style={styles.equipmentList}>
              {activity.required_equipment.map(
                (equipment: string, index: number) => (
                  <Chip
                    key={index}
                    mode='outlined'
                    compact
                    style={styles.equipmentChip}
                  >
                    {equipment}
                  </Chip>
                )
              )}
            </View>
          </View>
        )}

        {/* Difficulty Level */}
        <View style={styles.difficultySection}>
          <Text variant='labelMedium' style={styles.difficultyLabel}>
            Difficulty:
          </Text>
          <View style={styles.difficultyIndicator}>
            {[1, 2, 3, 4, 5].map(level => (
              <View
                key={level}
                style={[
                  styles.difficultyDot,
                  {
                    backgroundColor:
                      level <= activity.difficulty_level * 5
                        ? theme.colors.primary
                        : theme.colors.outline,
                  },
                ]}
              />
            ))}
          </View>
        </View>
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  backButton: {
    alignSelf: 'flex-start',
    marginTop: 16,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  header: {
    marginBottom: 24,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  description: {
    opacity: 0.7,
    lineHeight: 20,
  },
  progressCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressTitle: {
    fontWeight: 'bold',
  },
  progressPercentage: {
    fontWeight: 'bold',
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: 12,
  },
  progressStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    marginLeft: 6,
    opacity: 0.7,
  },
  objectivesCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  objectivesTitle: {
    fontWeight: 'bold',
    marginBottom: 12,
  },
  objectiveItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  objectiveText: {
    marginLeft: 8,
    flex: 1,
    lineHeight: 20,
  },
  activitiesSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    marginBottom: 16,
    fontWeight: 'bold',
  },
  activityCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  activityHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  activityIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  activityInfo: {
    flex: 1,
  },
  activityTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  activityMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    marginLeft: 4,
    opacity: 0.7,
  },
  activityStatus: {
    marginLeft: 12,
  },
  activityDescription: {
    opacity: 0.7,
    lineHeight: 18,
    marginBottom: 12,
  },
  activityProgress: {
    marginBottom: 12,
  },
  activityProgressBar: {
    height: 6,
    borderRadius: 3,
    marginBottom: 4,
  },
  progressText: {
    opacity: 0.7,
  },
  equipmentSection: {
    marginBottom: 12,
  },
  equipmentLabel: {
    marginBottom: 6,
    opacity: 0.7,
  },
  equipmentList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  equipmentChip: {
    marginRight: 4,
    marginBottom: 4,
  },
  difficultySection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  difficultyLabel: {
    opacity: 0.7,
  },
  difficultyIndicator: {
    flexDirection: 'row',
    gap: 4,
  },
  difficultyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  summaryCard: {
    padding: 16,
    borderRadius: 12,
  },
  summaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  summaryTitle: {
    marginLeft: 8,
    fontWeight: 'bold',
  },
  summaryText: {
    opacity: 0.7,
    lineHeight: 20,
    marginBottom: 16,
  },
  nextWeekButton: {
    alignSelf: 'flex-start',
  },
});
